import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Container,
  Button,
  CircularProgress,
} from "@mui/material";
import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";
import TimelineDot from "@mui/lab/TimelineDot";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import NoteIcon from "@mui/icons-material/Note";
import PaymentIcon from "@mui/icons-material/Payment";
import BusinessIcon from "@mui/icons-material/Business";
import { useDispatch, useSelector } from "react-redux";
import { fetchTransactions } from "../../../redux/actions/TransactionActions";
import { API_URI } from "../../../constants/GlobalConstants";
import fileDownload from "js-file-download";
import axios from "axios";
import DeleteConfirmation from "./DeleteConfirmation";
import { useSnackbarNotification } from "../../order/reusables/UseSnackBarNotification";

export default function TransactionTimeline({ id }) {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.userSignIn);
  const { transactions, loading, error } = useSelector(
    (state) => state.transactions
  );

  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  const { showSnackbar, SnackbarComponent } = useSnackbarNotification();

  const handleOpenModal = (transactionId) => {
    setSelectedTransactionId(transactionId);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedTransactionId(null);
    setModalOpen(false);
  };

  const monthOffset =
    selectedMonth -
    new Date().getMonth() +
    (selectedYear - new Date().getFullYear()) * 12;

  useEffect(() => {
    if (userInfo && userInfo.token) {
      dispatch(fetchTransactions(id, userInfo.token, 1, monthOffset));
    }
  }, [dispatch, userInfo, selectedMonth, selectedYear, monthOffset]);

  const getIcon = (mode) => {
    switch (mode) {
      case "Cash":
        return <AttachMoneyIcon />;
      case "Credit Card":
        return <CreditCardIcon />;
      case "Bank Transfer":
        return <AccountBalanceIcon />;
      case "Cheque":
        return <NoteIcon />;
      case "ERP Payment":
        return <PaymentIcon />;
      case "Bank Remittance":
        return <BusinessIcon />;
      default:
        return <AttachMoneyIcon />;
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const options = { year: "numeric", month: "long", day: "numeric" };
    const formattedDate = date.toLocaleDateString(undefined, options);
    const formattedTime = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    return { date: formattedDate, time: formattedTime };
  };

  // NEW — format only paymentDate:
  const formatOnlyDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handlePreviousMonth = () => {
    const prevMonth = (selectedMonth + 11) % 12;
    const prevYear = selectedMonth === 0 ? selectedYear - 1 : selectedYear;
    setSelectedMonth(prevMonth);
    setSelectedYear(prevYear);
  };

  const handleNextMonth = () => {
    const nextMonth = (selectedMonth + 1) % 12;
    const nextYear = selectedMonth === 11 ? selectedYear + 1 : selectedYear;
    setSelectedMonth(nextMonth);
    setSelectedYear(nextYear);
  };

  const handleMonthSelect = (event) => {
    const month = parseInt(event.target.getAttribute("data-month"), 10);
    setSelectedMonth(month);
  };

  const handleYearSelect = (event) => {
    const year = parseInt(event.target.getAttribute("data-year"), 10);
    setSelectedYear(year);
  };

  const getMonthName = (month) =>
    new Date(0, month).toLocaleString("default", { month: "long" });

  const currentMonthName = getMonthName(selectedMonth);
  const nextMonthName = getMonthName((selectedMonth + 1) % 12);
  const previousMonthName = getMonthName((selectedMonth + 11) % 12);

  const handlePrintTransactions = async () => {
    try {
      const response = await axios.get(
        `${API_URI}/api/transaction/transaction-pdf`,
        {
          params: { id, monthOffset },
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
            "Content-Type": "application/pdf",
          },
          responseType: "blob",
        }
      );
      fileDownload(response.data, "transactions.pdf");
    } catch (error) {
      console.error("Error downloading transactions:", error);
    }
  };

  const handleDeleteTransaction = async () => {
    try {
      const response = await axios.post(
        `${API_URI}/api/transaction/undotransaction`,
        { transactionId: selectedTransactionId },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userInfo.token}`,
          },
        }
      );

      if (response.status === 200) {
        showSnackbar(response.data.message, "success");
      }
    } catch (error) {
      showSnackbar("Unexpected error occurred.", "error");
    } finally {
      dispatch(fetchTransactions(id, userInfo.token, 1, monthOffset));
      handleCloseModal();
    }
  };

  return (
    <Box sx={{ backgroundColor: "#1e1e1e", minHeight: "100vh", py: 5 }}>
      <Container maxWidth="md">
        <Paper sx={{ p: 4, backgroundColor: "#1e1e1e", color: "white" }}>
          {/* Heading */}
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            sx={{
              fontSize: { xs: "1.5rem", sm: "2rem" },
              width: "100%",
            }}
          >
            Transaction History - {currentMonthName} {selectedYear}
          </Typography>

          {/* Month & Year Select */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              width: "100%",
              gap: 2,
            }}
          >
            <div className="dropdown d-inline-block">
              <Button
                className="btn btn-primary dropdown-toggle"
                data-bs-toggle="dropdown"
              >
                {currentMonthName}
              </Button>
              <ul className="dropdown-menu">
                {Array.from({ length: 12 }, (_, i) => (
                  <li key={i}>
                    <a
                      className="dropdown-item"
                      href="#"
                      data-month={i}
                      onClick={handleMonthSelect}
                    >
                      {new Date(0, i).toLocaleString("default", {
                        month: "long",
                      })}
                    </a>
                  </li>
                ))}
              </ul>

              <Button
                className="btn btn-primary dropdown-toggle"
                data-bs-toggle="dropdown"
              >
                {selectedYear}
              </Button>
              <ul className="dropdown-menu">
                {Array.from({ length: 5 }, (_, i) => (
                  <li key={i}>
                    <a
                      className="dropdown-item"
                      href="#"
                      data-year={2020 + i}
                      onClick={handleYearSelect}
                    >
                      {2020 + i}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <Button
              onClick={handlePrintTransactions}
              variant="contained"
              color="secondary"
            >
              Print Transactions
            </Button>
          </Box>

          {/* Timeline */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress color="primary" />
            </Box>
          ) : error ? (
            <Typography color="error" align="center">
              Error fetching transactions: {error}
            </Typography>
          ) : (
            <>
              <Timeline position="right">
                {transactions.map((transaction, index) => {
                  const { date, time } = formatDateTime(transaction.createdAt);

                  return (
                    <TimelineItem key={index}>
                      <TimelineOppositeContent
                        sx={{ m: "auto 0", color: "gray", fontSize: "1rem" }}
                      >
                        {date}
                      </TimelineOppositeContent>

                      <TimelineSeparator>
                        <TimelineConnector sx={{ backgroundColor: "white" }} />
                        <TimelineDot color="primary">
                          {getIcon(transaction.modeOfPayment)}
                        </TimelineDot>
                        <TimelineConnector sx={{ backgroundColor: "white" }} />
                      </TimelineSeparator>

                      <TimelineContent sx={{ py: "12px", px: 2 }}>
                        <Typography
                          variant="h6"
                          sx={{ color: "white", fontSize: "1.25rem" }}
                        >
                          ${transaction.amount} Received
                        </Typography>

                        {/* Payment time & method */}
                        <Typography sx={{ color: "gray", fontSize: "1rem" }}>
                          {time} —{" "}
                          {transaction.modeOfPayment ||
                            "Unknown Payment Method"}
                        </Typography>

                        {/* NEW — Payment Date */}
                        {transaction.paymentDate && (
                          <Typography
                            sx={{
                              color: "lightgray",
                              fontSize: "0.95rem",
                              mt: 0.5,
                            }}
                          >
                            Payment Date:{" "}
                            {formatOnlyDate(transaction.paymentDate)}
                          </Typography>
                        )}

                        {/* NEW — Remarks */}
                        {transaction.remarks &&
                          transaction.remarks.trim() !== "" && (
                            <Typography
                              sx={{
                                color: "#e0e0e0",
                                fontSize: "0.95rem",
                                mt: 0.5,
                              }}
                            >
                              Remarks: {transaction.remarks}
                            </Typography>
                          )}

                        {/* Order Details */}
                        <Box
                          sx={{
                            mt: 2,
                            pl: 2,
                            borderLeft: "2px solid lightgray",
                            color: "lightgray",
                          }}
                        >
                          {transaction.orderDetails.map(
                            (order, orderIndex) => (
                              <Box
                                key={orderIndex}
                                sx={{
                                  mb: 2,
                                  p: 2,
                                  backgroundColor: "#333",
                                  borderRadius: "8px",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "1rem",
                                    fontWeight: "bold",
                                  }}
                                >
                                  Product Type: {order.productType}
                                </Typography>

                                <Typography sx={{ fontSize: "1rem" }}>
                                  Tag: {order.tag || "No Tag"}
                                </Typography>

                                <Typography sx={{ fontSize: "1rem" }}>
                                  Amount Applied: $
                                  {order.transactions.amountApplied.toFixed(2)}
                                </Typography>

                                <Typography sx={{ fontSize: "1rem" }}>
                                  Closing Balance: $
                                  {order.outstandingAmount.toFixed(2)}
                                </Typography>

                                {order.openingBalance !== null &&
                                  order.openingBalance !== undefined && (
                                    <>
                                      <Typography sx={{ fontSize: "1rem" }}>
                                        Opening Balance: $
                                        {order.openingBalance.toFixed(2)}
                                      </Typography>
                                      <Typography sx={{ fontSize: "1rem" }}>
                                        Payment: ${order.payment}
                                      </Typography>
                                    </>
                                  )}
                              </Box>
                            )
                          )}
                        </Box>

                        {/* Delete Button */}
                        <Box
                          sx={{
                            mt: 2,
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() =>
                              handleOpenModal(transaction._id)
                            }
                          >
                            Delete Transaction
                          </Button>
                        </Box>
                      </TimelineContent>
                    </TimelineItem>
                  );
                })}
              </Timeline>

              {/* Bottom Navigation */}
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <Button
                  onClick={handlePreviousMonth}
                  variant="contained"
                  sx={{ mr: 2 }}
                >
                  {previousMonthName}
                </Button>

                {monthOffset < 0 && (
                  <Button onClick={handleNextMonth} variant="contained">
                    {nextMonthName}
                  </Button>
                )}
              </Box>
            </>
          )}
        </Paper>
      </Container>

      <SnackbarComponent />

      <DeleteConfirmation
        open={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleDeleteTransaction}
        title="Confirm Delete"
        description="Are you sure you want to delete this transaction? This action cannot be undone."
      />
    </Box>
  );
}
